import { useState, useEffect } from 'react';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import zipObject from 'lodash/zipObject';

const buildURLQuery = query =>
  Object.entries(query)
    .map(pair => pair.map(encodeURIComponent).join('='))
    .join('&');

const zipAttributes = attributes =>
  zipObject(
    attributes.map(attribute => camelCase(attribute.attribute_code)),
    attributes.map(attribute => attribute.value)
  );

const useFetchProducts = (storeId, skus = '', withCategory = false) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const query = {
    'searchCriteria[filterGroups][0][filters][0][field]': 'sku',
    'searchCriteria[filterGroups][0][filters][0][value]': skus,
    'searchCriteria[filterGroups][0][filters][0][conditionType]': 'in',
    'searchCriteria[filterGroups][1][filters][0][field]': 'store',
    'searchCriteria[filterGroups][1][filters][0][value]': storeId
  };

  const fetchOptions = {
    method: 'GET',
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  };

  const baseUrl = `${process.env.RAZZLE_MAGENTO_URL}/rest/${storeId}/V1`;

  async function fetchData() {
    const response = await fetch(
      `${baseUrl}/products?${buildURLQuery(query)}`,
      fetchOptions
    );
    const json = await response.json();

    const products = json.items
      ? json.items.map(product => ({
          ...product,
          attributes: zipAttributes(product.custom_attributes)
        }))
      : [];

    if (withCategory && products.length) {
      const categoryMemory = {};

      // first extract all needed categories to save some roundtrips
      const categoryIds = products.reduce((ids, product) => {
        const id = get(product, 'attributes.categoryIds[0]', null);
        // just keeping a category reference on the product to easy replace it later with the category object
        product.category = id;
        return id ? [...ids, id] : ids;
      }, []);

      for (const id of categoryIds) {
        const categoryResponse = await fetch(
          `${baseUrl}/categories/${id}`,
          fetchOptions
        );

        const category = await categoryResponse.json();

        if (category) {
          categoryMemory[id] = {
            ...category,
            attributes: zipAttributes(category.custom_attributes)
          };
        }
      }

      products.forEach(product => {
        product.category = categoryMemory[product.category];
      });
    }

    setData(products);
    setLoading(false);
  }

  useEffect(
    () => {
      if (storeId && skus) {
        fetchData();
      } else {
        setLoading(false);
      }
    },
    [storeId, skus]
  );

  return { loading, data };
};

export default useFetchProducts;
